// frontend/src/components/FinanceDepartmentDashboard.js
//
// Finance department dashboard (/departments/finance): total commissions by
// department for a quarter, split by how each plan pays.
//   • Monthly plans (Sales AEs) — the actual monthly payout rows, with status.
//   • Quarterly plans (Sales managers, Director of Operations) — progress so far,
//     so Finance can accrue the quarter-to-date amount.
// Numbers come from /api/payouts/by-department; nothing is calculated here.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { getCommissionsByDepartment } from '../api/client';

const fmt  = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmt0 = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const pct  = (x, d = 1) => x == null ? '—' : `${(x * 100).toFixed(d)}%`;

function quarterOptions() {
  const now = new Date();
  const y = now.getUTCFullYear(), q = Math.ceil((now.getUTCMonth() + 1) / 3);
  const out = [];
  for (let i = 0; i < 8; i++) {
    let yy = y, qq = q - i;
    while (qq <= 0) { qq += 4; yy--; }
    out.push(`${yy}-Q${qq}`);
  }
  return out;
}
const STATUS_BADGE = { pending: 'badge-pending', reviewed: 'badge-reviewed', approved: 'badge-approved', paid: 'badge-paid', disputed: 'badge-disputed', clawback: 'badge-disputed' };

export default function FinanceDepartmentDashboard({ members = [], label = 'Finance', isMine = false }) {
  const options = useMemo(quarterOptions, []);
  const [quarter, setQuarter] = useState(options[0]);
  const { data, loading, error } = useApi(() => getCommissionsByDepartment(quarter), [quarter]);

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>Department{isMine ? ' · yours' : ''} · commissions by department</div>
          <h1 style={{ fontSize: '1.6rem' }}>{label}</h1>
        </div>
        <select value={quarter} onChange={e => setQuarter(e.target.value)} className="input" style={{ width: 'auto', minWidth: '130px' }}>
          {options.map(q => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>

      {loading && !data ? (
        <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>
      ) : error && !data ? (
        <div className="card" style={{ padding: '24px' }}><h3 style={{ marginBottom: '6px' }}>Unavailable</h3><p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{String(error)}</p></div>
      ) : data ? (
        <Body data={data} />
      ) : null}

      <MembersCard label={label} members={members} />
    </div>
  );
}

function Body({ data }) {
  const { quarter, months, sales, quarterly = [], others, totals } = data;
  return (
    <>
      {/* ── Totals ── */}
      <div className="kpi-grid-4" style={{ gap: '12px', marginBottom: '20px' }}>
        <Tile label="Monthly plans · this quarter" value={fmt(totals.monthly)} sub="Sales AEs · actual monthly payouts" />
        <Tile label="Quarterly plans · accrue to date" value={fmt(totals.quarterlyAccrual)} sub="Leadership plans · projected from QTD results" accent="amber" />
        <Tile label="Total commissions" value={fmt(totals.grand)} sub={`${quarter} · monthly actuals + quarterly accruals`} accent="green" big />
        <Tile label="Awaiting sign-off" value={fmt(sales.byStatus.pending + sales.byStatus.reviewed)} sub={`${fmt(sales.byStatus.approved)} approved · ${fmt(sales.byStatus.paid)} paid`} accent="muted" />
      </div>

      {/* ── Sales AEs — monthly ── */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
        <CardTitle title="Sales — Account Executives" tag="paid monthly" sub={`${sales.plans.join(' · ')} · ${sales.repCount} reps with a payout this quarter`}
          right={<Link to="/finance" className="btn btn-ghost btn-sm">Open the approval queue →</Link>} />
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr><th>Month</th><th style={{ textAlign: 'right' }}>Reps</th><th>Status</th><th style={{ textAlign: 'right' }}>Commissions</th></tr>
            </thead>
            <tbody>
              {months.map(m => {
                const row = sales.months.find(x => x.month === m.key) || { total: 0, reps: 0, byStatus: {} };
                const statuses = Object.entries(row.byStatus || {}).filter(([, v]) => v.count > 0);
                return (
                  <tr key={m.key}>
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{m.label}</td>
                    <td className="td-number">{row.reps || '—'}</td>
                    <td>
                      {statuses.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: '12.5px' }}>no payouts yet</span> :
                        statuses.map(([s, v]) => <span key={s} className={`badge ${STATUS_BADGE[s] || ''}`} style={{ marginRight: '6px' }} title={fmt(v.total)}>{v.count} {s}</span>)}
                    </td>
                    <td className="td-number" style={{ fontWeight: 600, color: row.total > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{fmt(row.total)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: 'var(--bg-elevated)' }}>
                <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Quarter</td>
                <td className="td-number">{sales.repCount}</td>
                <td />
                <td className="td-number" style={{ fontWeight: 700, color: 'var(--green)', fontSize: '15px' }}>{fmt(sales.quarterTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Quarterly plans ── */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
        <CardTitle title="Quarterly plans — progress to date" tag="accrue these" sub="Projected from quarter-to-date results. Final amounts are set after quarter close; who accrues each plan comes from the leadership plan on Finance → Users." />
        {quarterly.length === 0 && <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No quarterly plans for this quarter.</div>}
        {quarterly.map((row, i) => {
          const d = describeQuarterly(row);
          return (
            <QuarterlyRow key={row.code} title={row.label} plan={row.plan} progressLabel={d.progressLabel} progress={row.progress}
              detail={`${fmt(row.perPayee)} × ${row.payees.length} ${row.payees.length === 1 ? 'payee' : 'payees'} (${row.payees.map(p => p.name).join(', ') || 'none assigned — set the leadership plan on Finance → Users'})${d.detail ? ' · ' + d.detail : ''}${row.error ? ' · ' + row.error : ''}`}
              amount={row.total} provisional={row.provisional} link={row.link ? { to: row.link, label: 'Open' } : null} last={i === quarterly.length - 1} />
          );
        })}
      </div>

      {/* ── Departments without a plan ── */}
      <div className="card" style={{ padding: '16px 24px', marginBottom: '20px' }}>
        <div className="label" style={{ marginBottom: '8px' }}>No comp plan configured</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {others.map(d => <span key={d.code} className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{d.label} · {d.members} {d.members === 1 ? 'member' : 'members'} · $0</span>)}
        </div>
      </div>
    </>
  );
}

// Plan-specific one-liners for the quarterly rows.
function describeQuarterly(row) {
  const m = row.meta || {};
  switch (row.code) {
    case 'sales_manager':
      return { progressLabel: `Quota attainment ${pct(row.progress, 1)} · ${fmt0(m.teamGP)} of ${fmt0(m.quotaGp)} team GP`, detail: `blended rate ${(m.blendedRate || 0).toFixed(2)}% of team GP` };
    case 'head_of_sales':
      return { progressLabel: `Closed-won GP ${pct(m.attainment, 1)} of the ${fmt0(m.floor)} Floor · ${fmt0(m.closedWonGp)}`, detail: `Component 1 ${fmt(m.comp1)} + Floor Bonus ${fmt(m.comp2)}${m.shortfall < 1 ? ` · shortfall factor ${(m.shortfall || 0).toFixed(3)}` : ''}${m.consecutiveMiss ? ' · consecutive miss × 0.75' : ''}` };
    case 'presales_director':
      return { progressLabel: `Weighted score ${pct(m.weighted, 1)} · company GP ${pct(m.gpFloor ? m.gp / m.gpFloor : 0, 1)} of the ${fmt0(m.gpFloor)} Floor (${m.cleared ? 'cleared' : 'missed'})`, detail: `min(score, ${pct(m.cap, 0)}) × ${(m.multiplier || 0).toFixed(3)} × ${fmt0(m.target)}` };
    case 'solutions_architect':
      return { progressLabel: `Weighted score ${pct(m.weighted, 1)} (E1–E3 provisional at target) · company GP ${pct(m.gpFloor ? m.gp / m.gpFloor : 0, 1)} of Floor`, detail: `Solutions Toolkit feed not connected — provisional` };
    case 'ops_director':
      return { progressLabel: `Services income ${pct(m.attainment, 1)} of the ${fmt0(m.floor)} Floor · ${m.rocksDone} of ${m.rocksTotal} Rocks complete`, detail: `Component 1 ${fmt(m.comp1)} + Rocks ${fmt(m.comp2)}${m.gateApplied ? ` × gate ${(m.gateMultiplier || 1).toFixed(3)}` : ''} · ${pct(m.pctOfTarget, 0)} of the ${fmt0(m.target)} target` };
    default:
      return { progressLabel: '', detail: '' };
  }
}

function QuarterlyRow({ title, plan, progressLabel, progress, detail, amount, link, last, provisional }) {
  const p = Math.max(0, Math.min(1, progress || 0));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '18px', alignItems: 'center', padding: '14px 0', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{plan}</span>
          {link && <Link to={link.to} style={{ fontSize: '12px' }}>{link.label} →</Link>}
        </div>
        <div style={{ position: 'relative', height: '8px', borderRadius: '4px', background: 'rgba(127,127,127,0.18)', margin: '8px 0 6px', overflow: 'hidden' }}>
          <div style={{ width: `${p * 100}%`, height: '100%', borderRadius: '4px', background: progress >= 1 ? 'var(--green)' : 'linear-gradient(90deg, #0079C2, #00A4E4)' }} />
        </div>
        <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>{progressLabel}</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{detail}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '20px', fontWeight: 700, color: amount > 0 ? 'var(--amber)' : 'var(--text-muted)' }}>{fmt(amount)}</div>
        <div style={{ fontSize: '11px', color: provisional ? 'var(--amber)' : 'var(--text-muted)' }}>{provisional ? 'provisional' : 'accrue to date'}</div>
      </div>
    </div>
  );
}

function Tile({ label, value, sub, accent = 'blue', big = false }) {
  const color = accent === 'green' ? 'var(--green)' : accent === 'amber' ? 'var(--amber)' : accent === 'muted' ? 'var(--text-muted)' : 'var(--blue-primary)';
  return (
    <div className="card" style={{ padding: '16px 18px', borderColor: big ? 'var(--border-bright)' : undefined }}>
      <div className="label" style={{ marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: big ? '26px' : '22px', fontWeight: 700, color, fontFamily: 'var(--font-mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}
function CardTitle({ title, tag, sub, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          {tag && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{tag}</span>}
        </div>
        {sub && <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}
const ROLE_LABEL = { rep: 'Rep', manager: 'Manager', finance_admin: 'Finance Admin', global_admin: 'Global Admin' };
function MembersCard({ label, members }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
        <h3>{label} team ({members.length})</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Department membership is set on Finance → Users.</p>
      </div>
      {members.length === 0 ? <div style={{ padding: '24px', color: 'var(--text-muted)', fontSize: '14px' }}>No one is assigned to {label} yet.</div> : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
          <tbody>{members.map(m => (
            <tr key={m.id} style={{ opacity: m.active ? 1 : 0.55 }}>
              <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{m.display_name || '—'}</td>
              <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{m.email || '—'}</td>
              <td>{ROLE_LABEL[m.app_role] || m.app_role}</td>
              <td><span className={`badge ${m.active ? 'badge-paid' : 'badge-disputed'}`}>{m.active ? 'Active' : 'Inactive'}</span></td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}
